import { toast } from "react-toastify";

import { useMutation } from "@apollo/client";

import { DELETE_FORECAST, FOLDERS, FOLDERS_LIGHT } from "../../../api/userArps";
import { ForecastActivityType, TYPE_WELLS } from "../../../constants";
import {
  selectCheckedForecastKeys,
  selectCheckedGlobalTypeWells,
  selectCheckedTypeWellKeys,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";
import { useSelectedProject } from "../../project/projects/hooks";

type idsToDelete = {
  folderId: string;
  id: string;
  type: string;
};

export const useDeleteForecast = (forecastType: ForecastActivityType) => {
  const { selectedProject: project } = useSelectedProject();

  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const setCheckedGlobalTypeWells = useForecastStore((s) => s.setCheckedGlobalTypeWells);
  const setCheckedTypeWellKeys = useForecastStore((s) => s.setCheckedTypeWellKeys);
  const setCheckedForecastKeys = useForecastStore((s) => s.setCheckedForecastKeys);
  const setCheckedTypeWells = useForecastStore((s) => s.setCheckedTypeWells);
  const setCheckedForecasts = useForecastStore((s) => s.setCheckedForecasts);
  const setSelectedTypeWell = useForecastStore((s) => s.setSelectedTypeWell);

  const checkedTypeWellKeys = useForecastStore(selectCheckedTypeWellKeys);
  const checkedForecastKeys = useForecastStore(selectCheckedForecastKeys);
  const checkedKeys = forecastType === TYPE_WELLS ? checkedTypeWellKeys : checkedForecastKeys;

  const updateGlobalTypeWells = async (
    idsToDelete: idsToDelete[],
    forecastType: ForecastActivityType
  ) => {
    const updatedGlobalTypeWells = checkedGlobalTypeWells.filter((well) => {
      return !idsToDelete.some((wellToDelete) => well.id === wellToDelete.id);
    });

    setCheckedGlobalTypeWells(updatedGlobalTypeWells);
    if (forecastType === TYPE_WELLS) {
      setCheckedTypeWellKeys(checkedKeys);
      setCheckedTypeWells(updatedGlobalTypeWells);
    } else {
      setCheckedForecastKeys(checkedKeys);
      setCheckedForecasts(updatedGlobalTypeWells);
    }

    const isWellBeingDeletedAlsoSelected = idsToDelete.some(
      (wellToDelete) => wellToDelete.id === selectedTypeWell?.id
    );

    if (isWellBeingDeletedAlsoSelected) {
      // Unselect and change the widget back to the selector tab.
      setSelectedTypeWell(null);
    }
  };

  const [deleteForecastMutation, { loading, error }] = useMutation(DELETE_FORECAST);

  const deleteForecast = async (idsToDelete: idsToDelete[]) => {
    const foldersQuery = forecastType === TYPE_WELLS ? FOLDERS : FOLDERS_LIGHT;
    await deleteForecastMutation({
      variables: {
        input: {
          ids: idsToDelete,
          projectId: project?.projectId,
          type: forecastType
        }
      },
      refetchQueries: [
        {
          query: foldersQuery,
          variables: { req: { projectId: project?.projectId, type: forecastType } }
        }
      ],
      onCompleted: async () => {
        await updateGlobalTypeWells(idsToDelete, forecastType);
      },
      onError: (err) => {
        toast.error(err.message);
      }
    });
  };

  return { delete: deleteForecast, loading, error };
};
